import React, { useState, useEffect } from "react";
import TableDiff from "./SubComponents/TableDiff";
import { useTableActions } from "./SubComponents/hooks/useTableActions";
import RecHeader from "./RecHeader";
import AddJobModal from "./AddJobModal"; // Updated import
import EditJobsModal from "./EditJobsModal"; // Add this import
import {
  recruitJobsApis,
  rolesSettingsApis,
  departmentHRApi,
  employeeHRApis,
} from "../../../Data/axios";
import { Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";
import JobViewMoreModal from "./JobViewMoreModal"; // Import the JobViewMoreModal component

export default function JobTableUI() {
  const [open, setOpen] = useState(false); //add job modal

  // States for API data
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });

  // Filter state
  const [filters, setFilters] = useState({
    dateFilter: "Start Date",
    department: "All",
    status: "All",
    recruiter_name: "All",
    // dateRange: "Start Date To End Date",
    title: "",
  });

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    console.log(`Filter changed - ${filterType}: ${value}`);

    if (filterType === "clearAll") {
      // Reset all filters to default values
      setFilters({
        dateFilter: "Start Date",
        department: "All",
        status: "All",
        recruiter_name: "All",
        title: "",
      });
      return;
    }

    if (filterType === "dateRange") {
      console.log("Handling date range filter change:", value);
      // Handle both dates at once, remove dateRange entirely
      setFilters((prev) => {
        const { dateRange, ...restFilters } = prev; // Destructure to remove dateRange
        return {
          ...restFilters,
          from_date: value.from_date,
          to_date: value.to_date,
        };
      });
    } else {
      // Handle other filters normally
      setFilters((prev) => ({
        ...prev,
        [filterType]: value,
      }));
    }

    console.log("Updated filters:", {
      ...filters,
      [filterType]: value,
    });
  };

  // Fetch data on component mount
  const fetchData = async (params = {}) => {
    try {
      setLoading(true);

      // Build filter parameters, only include non-default values
      const filterParams = {};

      // Only add filters if they're not the default values
      if (filters.title && filters.title.trim() !== "") {
        filterParams.title = filters.title;
      }

      if (filters.department && filters.department !== "All") {
        filterParams.department = filters.department;
      }

      if (filters.status && filters.status !== "All") {
        // Convert status back to API format (1 for Open, 0 for Closed)
        filterParams.status = filters.status === "Open" ? 1 : 0;
      }

      if (filters.recruiter_name && filters.recruiter_name !== "All") {
        filterParams.recruiter_name = filters.recruiter_name;
      }

      // Handle date filters
      if (filters.from_date && filters.from_date !== "") {
        filterParams.from_date = filters.from_date;
      }

      if (filters.to_date && filters.to_date !== "") {
        filterParams.to_date = filters.to_date;
      }

      // Combine all parameters
      const apiParams = {
        page: params.page || queries.page,
        per_page: params.per_page || queries.per_page,
        ...filterParams,
        ...params,
      };

      console.log("API parameters being sent:", apiParams);

      const jobTitlesResponse = await recruitJobsApis.getAllJobTitle(apiParams);
      console.log("before formatting", jobTitlesResponse.data.rjobs.data);
      // Format the data
      const formattedData = jobTitlesResponse.data.rjobs.data.map((job) => ({
        ...job,
        start_date: job.start_date ? job.start_date.split("T")[0] : "",
        end_date: job.end_date ? job.end_date.split("T")[0] : "",
        status: job.status === 1 ? "Open" : "Closed",
        recruiter_empid: job.recruiter?.empid || "",
        recruiter_name: job.recruiter?.name || "",
      }));

      console.log("Formatted Job Titles Data:", formattedData);
      setJobTitles(formattedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching skills:", error);
      setJobTitles([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [queries.page, queries.per_page, filters]);

  const tableActions = useTableActions(recruitJobsApis, fetchData);
  const {
    showEdit,
    editData,
    setShowEdit,
    setEditData,
    showViewMore,
    viewData,
    setShowViewMore,
    setViewData,
  } = tableActions;

  // Handle modal open/close
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Handle save job - refresh data after adding new job
  const handleSaveJob = async () => {
    try {
      // Refresh job titles data
      const jobTitlesResponse = await recruitJobsApis.getAllJobTitle();
      setJobTitles(jobTitlesResponse.data.rjobs.data);
    } catch (err) {
      console.error("Error refreshing data:", err);
    }
  };

  //table settings
  const headers = [
    { key: "title", label: "Job Title", sortable: true },
    { key: "recruiter_empid", label: "Recruiter ID", sortable: true },
    { key: "recruiter_name", label: "Recruiter Name", sortable: true },
    { key: "start_date", label: "Start Date", sortable: true },
    { key: "end_date", label: "End Date", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const handlePageChange = (newPage) => {
    setQueries({ ...queries, page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setQueries({ ...queries, per_page: newPerPage, page: 1 });
  };

  return (
    <div>
      <RecHeader onFilterChange={handleFilterChange} filters={filters} />
      <Box 
        display="flex" 
        justifyContent="flex-start"
        sx={{ marginBottom: "16px" }}
      >
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpen}
          sx={{
            bgcolor: "#4285F4",
            color: "white",
            "&:hover": {
              bgcolor: "#3367D6",
            },
            marginTop: "10px",
            marginLeft: "16px",
            paddingY: "8px",
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          Add Job
        </Button>
      </Box>

      <TableDiff
        headers={headers}
        data={jobTitles}
        rowKey="id"
        defaultSortKey=""
        tableActions={tableActions}
        tableApis={recruitJobsApis}
        onTableUpdate={fetchData}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        queries={queries}
        type="jobs"
        tableClassName="jobs-table"
        tableStyle={{ fontSize: "16px", fontFamily: "Ubuntu" }}
      />

      {/* Use the new AddJobModal component */}
      <AddJobModal
        open={open}
        handleClose={handleClose}
        handleSave={handleSaveJob}
        tableApis={recruitJobsApis}
      />

      {/* Add the JobViewMoreModal component */}
      <JobViewMoreModal
        open={showViewMore}
        handleClose={() => setShowViewMore(false)}
        viewData={viewData}
      />

      {/* Add the EditJobsModal component */}
      <EditJobsModal
        open={showEdit}
        handleClose={() => setShowEdit(false)}
        handleSave={fetchData}
        tableApis={recruitJobsApis}
        editData={editData}
      />
    </div>
  );
}